// What the account menu's body is handed, and its two parts: the account's
// figures as cells, and the pages that are not there yet.

import { type Icon, PaintBrushIcon, ShieldCheckIcon } from '@phosphor-icons/react'
import { formatCompactExact } from '../../lib/format'
import { figs } from '../ui/fig'
import { GramMark } from '../ui/icons/GramMark'
import { type AccountFigures, DASH, pointsText } from './accountFigures'
import styles from './profile.module.css'

export type AccountBodyProps = {
  figures: AccountFigures
  /** A signed-in account whose keyring this browser cannot open: the way back
   *  in is offered, and takes the place of the Security page that is not there. */
  restorable: boolean
  onSettings: () => void
  onRestore: () => void
  /** Opens What's New; the row is drawn only when there is a way to open it. */
  onWhatsNew?: () => void
}

// What the account has, as four cells — a name over its figure, short dividers
// between them, as the lab's position strip (Bought / Sold / Holding / PnL).
// A figure the session does not know is a dash, never a zero. Nothing is
// drawn for a menu with no signed-in account behind it.
export function FigureCells({ figures }: { figures: AccountFigures }) {
  if (!figures.known) return null
  const rewards = figures.rewards
  return (
    <dl className={styles.figures}>
      <div className={styles.cell}>
        <dt>Wallets</dt>
        <dd>{figs(figures.wallets)}</dd>
      </div>
      <div className={styles.cell}>
        <dt>Tokens</dt>
        <dd>{figs(figures.tokens ?? DASH)}</dd>
      </div>
      <div className={styles.cell}>
        <dt>Points</dt>
        <dd>{figs(pointsText(rewards) ?? DASH)}</dd>
      </div>
      <div className={styles.cell}>
        <dt>Rewards</dt>
        <dd>
          {rewards ? (
            <>
              <GramMark size={12} />
              {figs(formatCompactExact(rewards.total_rewards_claimable_gram))}
            </>
          ) : (
            figs(DASH)
          )}
        </dd>
      </div>
    </dl>
  )
}

// Appearance and Security have no page. Each stands on its own row, in the
// rows' own measure (the mark, 10, the name), said plainly and not a control —
// nothing here can be pressed or focused — and ends in the tag the Settings
// dialog gives what it cannot do yet.
export function SoonRows({ restorable }: { restorable: boolean }) {
  return (
    <ul className={styles.soonList} aria-label="Not available yet">
      <SoonRow icon={PaintBrushIcon} label="Appearance" />
      {restorable ? null : <SoonRow icon={ShieldCheckIcon} label="Security" />}
    </ul>
  )
}

function SoonRow({ icon: I, label }: { icon: Icon; label: string }) {
  return (
    <li className={styles.soonRow}>
      <I size={18} weight="bold" aria-hidden="true" />
      <span className={styles.soonLabel}>{label}</span>
      <span className={styles.soonTag}>{figs('Soon')}</span>
    </li>
  )
}
